import type { ReactNode } from 'react';
import { alpha, Box, Chip, Grid, Paper, Stack, Typography } from '@mui/material';
import AccountTreeOutlinedIcon from '@mui/icons-material/AccountTreeOutlined';
import ArrowForwardRoundedIcon from '@mui/icons-material/ArrowForwardRounded';
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded';
import ErrorOutlineRoundedIcon from '@mui/icons-material/ErrorOutlineRounded';
import FactoryOutlinedIcon from '@mui/icons-material/FactoryOutlined';
import Inventory2OutlinedIcon from '@mui/icons-material/Inventory2Outlined';
import LayersOutlinedIcon from '@mui/icons-material/LayersOutlined';
import PaidOutlinedIcon from '@mui/icons-material/PaidOutlined';
import PaymentsOutlinedIcon from '@mui/icons-material/PaymentsOutlined';
import PrecisionManufacturingOutlinedIcon from '@mui/icons-material/PrecisionManufacturingOutlined';
import ReceiptLongOutlinedIcon from '@mui/icons-material/ReceiptLongOutlined';
import RouteOutlinedIcon from '@mui/icons-material/RouteOutlined';
import ShoppingCartOutlinedIcon from '@mui/icons-material/ShoppingCartOutlined';
import TuneOutlinedIcon from '@mui/icons-material/TuneOutlined';
import WarningAmberRoundedIcon from '@mui/icons-material/WarningAmberRounded';

const COLORS = {
  blue: '#38bdf8',
  cyan: '#22d3ee',
  amber: '#f59e0b',
  green: '#22c55e',
  purple: '#a78bfa',
  rose: '#fb7185',
  slate: '#94a3b8',
};

type GuideCardProps = {
  eyebrow?: string;
  title: string;
  description: string;
  icon?: ReactNode;
  accentColor?: string;
};

function GuideCard({
  eyebrow,
  title,
  description,
  icon,
  accentColor = COLORS.blue,
}: GuideCardProps) {
  return (
    <Paper
      elevation={0}
      sx={{
        height: '100%',
        borderRadius: 4,
        border: `1px solid ${alpha(accentColor, 0.16)}`,
        bgcolor: 'rgba(8,18,36,0.88)',
        backdropFilter: 'blur(10px)',
        boxShadow: `inset 0 1px 0 ${alpha('#ffffff', 0.025)}`,
        px: 2.4,
        py: 2.25,
      }}
    >
      <Stack spacing={1.15}>
        {(eyebrow || icon) ? (
          <Stack direction="row" spacing={1} alignItems="center">
            {icon ? (
              <Box
                sx={{
                  width: 34,
                  height: 34,
                  borderRadius: 2,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  bgcolor: alpha(accentColor, 0.1),
                  color: accentColor,
                  border: `1px solid ${alpha(accentColor, 0.18)}`,
                  flexShrink: 0,
                }}
              >
                {icon}
              </Box>
            ) : null}

            {eyebrow ? (
              <Typography
                variant="overline"
                sx={{
                  color: accentColor,
                  fontWeight: 850,
                  letterSpacing: 1.15,
                  lineHeight: 1,
                }}
              >
                {eyebrow}
              </Typography>
            ) : null}
          </Stack>
        ) : null}

        <Typography
          variant="h6"
          sx={{
            color: '#f8fafc',
            fontWeight: 950,
            lineHeight: 1.15,
            letterSpacing: '-0.3px',
          }}
        >
          {title}
        </Typography>

        <Typography
          variant="body2"
          sx={{
            color: '#94a3b8',
            lineHeight: 1.7,
          }}
        >
          {description}
        </Typography>
      </Stack>
    </Paper>
  );
}

type FlowStepProps = {
  index: number;
  title: string;
  description: string;
  icon?: ReactNode;
  accentColor?: string;
};

function FlowStep({
  index,
  title,
  description,
  icon,
  accentColor = COLORS.blue,
}: FlowStepProps) {
  return (
    <Paper
      elevation={0}
      sx={{
        height: '100%',
        borderRadius: 4,
        border: `1px solid ${alpha(accentColor, 0.16)}`,
        bgcolor: 'rgba(15,23,42,0.58)',
        px: 2.2,
        py: 2,
      }}
    >
      <Stack spacing={1.1}>
        <Stack direction="row" spacing={1.1} alignItems="center">
          <Box
            sx={{
              width: 30,
              height: 30,
              borderRadius: '999px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              bgcolor: alpha(accentColor, 0.12),
              color: accentColor,
              border: `1px solid ${alpha(accentColor, 0.2)}`,
              fontWeight: 950,
              fontSize: '0.82rem',
              flexShrink: 0,
            }}
          >
            {index}
          </Box>
          {icon ? (
            <Box sx={{ color: accentColor, display: 'flex', alignItems: 'center' }}>
              {icon}
            </Box>
          ) : null}
        </Stack>

        <Typography sx={{ color: '#f8fafc', fontWeight: 900, lineHeight: 1.2 }}>
          {title}
        </Typography>

        <Typography variant="body2" sx={{ color: '#94a3b8', lineHeight: 1.65 }}>
          {description}
        </Typography>
      </Stack>
    </Paper>
  );
}

function FlowArrow() {
  return (
    <Box
      sx={{
        display: { xs: 'none', md: 'flex' },
        justifyContent: 'center',
        alignItems: 'center',
        color: '#475569',
        px: 0.5,
      }}
    >
      <ArrowForwardRoundedIcon />
    </Box>
  );
}

type StatusCardProps = {
  title: string;
  description: string;
  icon: ReactNode;
  accentColor: string;
};

function StatusCard({ title, description, icon, accentColor }: StatusCardProps) {
  return (
    <Paper
      elevation={0}
      sx={{
        height: '100%',
        borderRadius: 3,
        border: `1px solid ${alpha(accentColor, 0.18)}`,
        bgcolor: alpha(accentColor, 0.065),
        px: 2,
        py: 1.8,
      }}
    >
      <Stack spacing={1.1}>
        <Stack direction="row" spacing={1} alignItems="center">
          <Box sx={{ color: accentColor, display: 'flex' }}>{icon}</Box>
          <Typography sx={{ color: '#f8fafc', fontWeight: 900 }}>
            {title}
          </Typography>
        </Stack>
        <Typography variant="body2" sx={{ color: '#cbd5e1', lineHeight: 1.6 }}>
          {description}
        </Typography>
      </Stack>
    </Paper>
  );
}

export default function IndustrialCostGuideView() {
  return (
    <Stack spacing={3}>
      <Paper
        elevation={0}
        sx={{
          borderRadius: 4,
          border: `1px solid ${alpha(COLORS.blue, 0.18)}`,
          bgcolor: 'rgba(8,18,36,0.88)',
          backdropFilter: 'blur(10px)',
          boxShadow: `inset 0 1px 0 ${alpha('#ffffff', 0.025)}`,
          px: { xs: 2.5, md: 3.25 },
          py: { xs: 2.75, md: 3.25 },
          overflow: 'hidden',
          position: 'relative',
        }}
      >
        <Box
          sx={{
            position: 'absolute',
            right: -80,
            top: -90,
            width: 260,
            height: 260,
            borderRadius: '999px',
            bgcolor: alpha(COLORS.blue, 0.08),
            filter: 'blur(2px)',
          }}
        />

        <Stack
          direction={{ xs: 'column', lg: 'row' }}
          spacing={3}
          justifyContent="space-between"
          alignItems={{ xs: 'flex-start', lg: 'center' }}
          sx={{ position: 'relative', zIndex: 1 }}
        >
          <Stack spacing={1.25} sx={{ maxWidth: 860 }}>
            <Typography
              variant="overline"
              sx={{ color: COLORS.blue, fontWeight: 900, letterSpacing: 1.35, lineHeight: 1 }}
            >
              PROA Costing · Guía de funcionamiento
            </Typography>
            <Typography
              variant="h4"
              sx={{
                color: '#f8fafc',
                fontWeight: 950,
                lineHeight: 1.08,
                letterSpacing: '-0.8px',
              }}
            >
              Cómo PROA reconstruye el coste industrial de cada producto
            </Typography>
            <Typography variant="body1" sx={{ color: '#cbd5e1', lineHeight: 1.75 }}>
              Esta guía explica la lógica de la aplicación: qué datos entran, cómo se transforman
              en coste y cómo se interpreta el resultado final. No es una wiki técnica; es el mapa
              operativo para entender el sistema.
            </Typography>
          </Stack>

          <Paper
            elevation={0}
            sx={{
              minWidth: { xs: '100%', sm: 320 },
              borderRadius: 3,
              border: `1px solid ${alpha(COLORS.green, 0.24)}`,
              bgcolor: alpha(COLORS.green, 0.08),
              p: 2,
            }}
          >
            <Typography variant="caption" sx={{ color: '#86efac', fontWeight: 850 }}>
              Resultado buscado
            </Typography>
            <Typography variant="h6" sx={{ color: '#f8fafc', fontWeight: 950, mt: 0.5 }}>
              Coste por producto y mes
            </Typography>
            <Typography variant="body2" sx={{ color: '#bbf7d0', mt: 0.75, lineHeight: 1.55 }}>
              Coste total, coste unitario, desglose por capas y avisos de calidad del cálculo.
            </Typography>
          </Paper>
        </Stack>
      </Paper>

      <Paper
        elevation={0}
        sx={{
          borderRadius: 4,
          border: `1px solid ${alpha('#94a3b8', 0.16)}`,
          bgcolor: 'rgba(8,18,36,0.88)',
          backdropFilter: 'blur(10px)',
          px: { xs: 2.5, md: 3 },
          py: { xs: 2.5, md: 3 },
        }}
      >
        <Stack spacing={2.25}>
          <Box>
            <Typography variant="overline" sx={{ color: COLORS.blue, fontWeight: 850, letterSpacing: 1.2 }}>
              Idea central
            </Typography>
            <Typography variant="h6" sx={{ color: '#f8fafc', fontWeight: 950, mt: 0.8 }}>
              PROA no sustituye al ERP: conecta datos dispersos para explicar el coste
            </Typography>
            <Typography variant="body2" sx={{ color: '#94a3b8', mt: 0.8, lineHeight: 1.75, maxWidth: 980 }}>
              La aplicación toma datos que normalmente viven separados —producción, compras,
              inventario, recetas técnicas, mano de obra y movimientos económicos— y los convierte
              en una lectura trazable del coste de fabricación por producto.
            </Typography>
          </Box>

          <Grid container spacing={1.5}>
            <Grid item xs={12} md={4}>
              <GuideCard
                eyebrow="Entrada"
                title="Datos reales del periodo"
                description="Producción del mes, entradas de materiales, inventario físico, movimientos económicos y configuración laboral."
                icon={<ReceiptLongOutlinedIcon fontSize="small" />}
                accentColor={COLORS.blue}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <GuideCard
                eyebrow="Interpretación"
                title="Modelo industrial configurable"
                description="Productos, etapas, reglas técnicas, aplicabilidad, valoración de materiales, mano de obra y reglas de reparto."
                icon={<TuneOutlinedIcon fontSize="small" />}
                accentColor={COLORS.purple}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <GuideCard
                eyebrow="Salida"
                title="Coste explicable"
                description="Coste total, coste unitario, composición por capas y avisos cuando el resultado es parcial o necesita revisión."
                icon={<PaidOutlinedIcon fontSize="small" />}
                accentColor={COLORS.green}
              />
            </Grid>
          </Grid>
        </Stack>
      </Paper>

      <Paper
        elevation={0}
        sx={{
          borderRadius: 4,
          border: `1px solid ${alpha(COLORS.cyan, 0.16)}`,
          bgcolor: 'rgba(8,18,36,0.88)',
          backdropFilter: 'blur(10px)',
          px: { xs: 2.5, md: 3 },
          py: { xs: 2.5, md: 3 },
        }}
      >
        <Stack spacing={2.25}>
          <Box>
            <Typography variant="overline" sx={{ color: COLORS.cyan, fontWeight: 850, letterSpacing: 1.2 }}>
              Flujo de reconstrucción
            </Typography>
            <Typography variant="h6" sx={{ color: '#f8fafc', fontWeight: 950, mt: 0.8 }}>
              Del dato operativo al coste final
            </Typography>
          </Box>

          <Grid container spacing={1.25} alignItems="stretch">
            <Grid item xs={12} md>
              <FlowStep
                index={1}
                title="Producción"
                description="Define qué productos se fabricaron y en qué cantidad durante el mes."
                icon={<FactoryOutlinedIcon fontSize="small" />}
                accentColor={COLORS.blue}
              />
            </Grid>
            <Grid item md="auto"><FlowArrow /></Grid>
            <Grid item xs={12} md>
              <FlowStep
                index={2}
                title="Consumo técnico"
                description="Convierte producción en consumo esperado mediante reglas producto-material."
                icon={<PrecisionManufacturingOutlinedIcon fontSize="small" />}
                accentColor={COLORS.amber}
              />
            </Grid>
            <Grid item md="auto"><FlowArrow /></Grid>
            <Grid item xs={12} md>
              <FlowStep
                index={3}
                title="Valoración"
                description="Valora materiales con PMP, valoración manual o último precio de compra cuando aplica."
                icon={<Inventory2OutlinedIcon fontSize="small" />}
                accentColor={COLORS.green}
              />
            </Grid>
            <Grid item md="auto"><FlowArrow /></Grid>
            <Grid item xs={12} md>
              <FlowStep
                index={4}
                title="Capas de coste"
                description="Suma material directo, mano de obra e indirectos imputados al producto."
                icon={<LayersOutlinedIcon fontSize="small" />}
                accentColor={COLORS.purple}
              />
            </Grid>
            <Grid item md="auto"><FlowArrow /></Grid>
            <Grid item xs={12} md>
              <FlowStep
                index={5}
                title="Resultado"
                description="Muestra coste total, coste unitario, desglose y avisos de fiabilidad."
                icon={<RouteOutlinedIcon fontSize="small" />}
                accentColor={COLORS.green}
              />
            </Grid>
          </Grid>
        </Stack>
      </Paper>

      <Grid container spacing={2.5}>
        <Grid item xs={12} lg={7}>
          <Paper
            elevation={0}
            sx={{
              height: '100%',
              borderRadius: 4,
              border: `1px solid ${alpha(COLORS.green, 0.16)}`,
              bgcolor: 'rgba(8,18,36,0.88)',
              backdropFilter: 'blur(10px)',
              px: { xs: 2.5, md: 3 },
              py: { xs: 2.5, md: 3 },
            }}
          >
            <Stack spacing={2.25}>
              <Box>
                <Typography variant="overline" sx={{ color: COLORS.green, fontWeight: 850, letterSpacing: 1.2 }}>
                  Capas del coste
                </Typography>
                <Typography variant="h6" sx={{ color: '#f8fafc', fontWeight: 950, mt: 0.8 }}>
                  Qué se suma para obtener el coste de fabricación
                </Typography>
              </Box>

              <Grid container spacing={1.5}>
                <Grid item xs={12} md={4}>
                  <GuideCard
                    title="Material directo"
                    description="Se obtiene desde producción × consumo técnico × precio valorado. Si falta clasificación, el coste se conserva como Sin clasificar."
                    icon={<Inventory2OutlinedIcon fontSize="small" />}
                    accentColor={COLORS.amber}
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <GuideCard
                    title="Mano de obra"
                    description="Puede imputarse por etapa o por producto. En producto, el sistema usa rendimiento y coste hora para calcular horas y coste."
                    icon={<PrecisionManufacturingOutlinedIcon fontSize="small" />}
                    accentColor={COLORS.green}
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <GuideCard
                    title="Coste indirecto"
                    description="Parte de movimientos económicos clasificados, repartos por etapas y reglas finales hacia productos aplicables."
                    icon={<AccountTreeOutlinedIcon fontSize="small" />}
                    accentColor={COLORS.purple}
                  />
                </Grid>
              </Grid>
            </Stack>
          </Paper>
        </Grid>

        <Grid item xs={12} lg={5}>
          <Paper
            elevation={0}
            sx={{
              height: '100%',
              borderRadius: 4,
              border: `1px solid ${alpha(COLORS.amber, 0.18)}`,
              bgcolor: 'rgba(8,18,36,0.88)',
              backdropFilter: 'blur(10px)',
              px: { xs: 2.5, md: 3 },
              py: { xs: 2.5, md: 3 },
            }}
          >
            <Stack spacing={2}>
              <Box>
                <Typography variant="overline" sx={{ color: '#fbbf24', fontWeight: 850, letterSpacing: 1.2 }}>
                  Lectura del resultado
                </Typography>
                <Typography variant="h6" sx={{ color: '#f8fafc', fontWeight: 950, mt: 0.8 }}>
                  El número no basta: hay que saber si es fiable
                </Typography>
                <Typography variant="body2" sx={{ color: '#94a3b8', mt: 0.8, lineHeight: 1.7 }}>
                  Resultados no solo muestra importes. También avisa cuando una capa falta, usa un fallback o necesita revisión.
                </Typography>
              </Box>

              <Grid container spacing={1.25}>
                <Grid item xs={12} sm={6}>
                  <StatusCard
                    title="Completo"
                    description="El coste se calcula sin avisos relevantes para el producto."
                    icon={<CheckCircleRoundedIcon fontSize="small" />}
                    accentColor={COLORS.green}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <StatusCard
                    title="Con avisos"
                    description="El coste existe, pero hay aspectos que revisar: sin clasificar, sin indirecto o fallback de valoración."
                    icon={<WarningAmberRoundedIcon fontSize="small" />}
                    accentColor={COLORS.amber}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <StatusCard
                    title="Parcial"
                    description="Falta una capa o dato esperado que afecta directamente a la lectura del coste."
                    icon={<ErrorOutlineRoundedIcon fontSize="small" />}
                    accentColor={COLORS.rose}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <StatusCard
                    title="Sin coste"
                    description="Hay producción o contexto, pero el sistema no puede construir un coste útil."
                    icon={<PaymentsOutlinedIcon fontSize="small" />}
                    accentColor={COLORS.slate}
                  />
                </Grid>
              </Grid>
            </Stack>
          </Paper>
        </Grid>
      </Grid>

      <Paper
        elevation={0}
        sx={{
          borderRadius: 4,
          border: `1px solid ${alpha(COLORS.purple, 0.16)}`,
          bgcolor: 'rgba(8,18,36,0.88)',
          backdropFilter: 'blur(10px)',
          px: { xs: 2.5, md: 3 },
          py: { xs: 2.5, md: 3 },
        }}
      >
        <Stack spacing={2.25}>
          <Box>
            <Typography variant="overline" sx={{ color: '#c4b5fd', fontWeight: 850, letterSpacing: 1.2 }}>
              Secciones de la aplicación
            </Typography>
            <Typography variant="h6" sx={{ color: '#f8fafc', fontWeight: 950, mt: 0.8 }}>
              Qué papel cumple cada zona de PROA Costing
            </Typography>
          </Box>

          <Grid container spacing={1.5}>
            <Grid item xs={12} md={6} xl={3}>
              <GuideCard
                title="Configuración"
                description="Define la estructura industrial: productos, etapas, reglas, líneas y parámetros base."
                icon={<TuneOutlinedIcon fontSize="small" />}
                accentColor={COLORS.blue}
              />
            </Grid>
            <Grid item xs={12} md={6} xl={3}>
              <GuideCard
                title="Materiales y valoración"
                description="Gestiona materiales, entradas, inventario físico y valoración económica del periodo."
                icon={<ShoppingCartOutlinedIcon fontSize="small" />}
                accentColor={COLORS.amber}
              />
            </Grid>
            <Grid item xs={12} md={6} xl={3}>
              <GuideCard
                title="Producción y mano de obra"
                description="Aporta volumen producido, rendimiento laboral y coste de mano de obra aplicable."
                icon={<FactoryOutlinedIcon fontSize="small" />}
                accentColor={COLORS.green}
              />
            </Grid>
            <Grid item xs={12} md={6} xl={3}>
              <GuideCard
                title="Resultados"
                description="Resume el coste final por producto y mes, con desglose y avisos de calidad."
                icon={<PaidOutlinedIcon fontSize="small" />}
                accentColor={COLORS.purple}
              />
            </Grid>
          </Grid>
        </Stack>
      </Paper>

      <Paper
        elevation={0}
        sx={{
          borderRadius: 4,
          border: `1px solid ${alpha(COLORS.blue, 0.16)}`,
          bgcolor: 'rgba(8,18,36,0.88)',
          backdropFilter: 'blur(10px)',
          px: { xs: 2.5, md: 3 },
          py: { xs: 2.5, md: 3 },
        }}
      >
        <Stack spacing={2}>
          <Box>
            <Typography variant="overline" sx={{ color: COLORS.blue, fontWeight: 850, letterSpacing: 1.2 }}>
              Alcance del producto
            </Typography>
            <Typography variant="h6" sx={{ color: '#f8fafc', fontWeight: 950, mt: 0.8 }}>
              Qué es PROA Costing y qué no pretende ser
            </Typography>
          </Box>

          <Grid container spacing={1.5}>
            <Grid item xs={12} md={6}>
              <Paper
                elevation={0}
                sx={{
                  height: '100%',
                  borderRadius: 3,
                  border: `1px solid ${alpha(COLORS.green, 0.18)}`,
                  bgcolor: alpha(COLORS.green, 0.055),
                  p: 2.25,
                }}
              >
                <Stack spacing={1.2}>
                  <Chip
                    label="Sí es"
                    size="small"
                    sx={{
                      alignSelf: 'flex-start',
                      color: '#bbf7d0',
                      bgcolor: alpha(COLORS.green, 0.14),
                      border: `1px solid ${alpha(COLORS.green, 0.24)}`,
                      fontWeight: 850,
                    }}
                  />
                  <Typography variant="body2" sx={{ color: '#cbd5e1', lineHeight: 1.75 }}>
                    Una capa de reconstrucción, trazabilidad y decisión sobre el coste industrial. Ayuda a explicar cómo se forma el coste real o aproximado de fabricación.
                  </Typography>
                </Stack>
              </Paper>
            </Grid>
            <Grid item xs={12} md={6}>
              <Paper
                elevation={0}
                sx={{
                  height: '100%',
                  borderRadius: 3,
                  border: `1px solid ${alpha(COLORS.rose, 0.18)}`,
                  bgcolor: alpha(COLORS.rose, 0.045),
                  p: 2.25,
                }}
              >
                <Stack spacing={1.2}>
                  <Chip
                    label="No es"
                    size="small"
                    sx={{
                      alignSelf: 'flex-start',
                      color: '#fecdd3',
                      bgcolor: alpha(COLORS.rose, 0.12),
                      border: `1px solid ${alpha(COLORS.rose, 0.22)}`,
                      fontWeight: 850,
                    }}
                  />
                  <Typography variant="body2" sx={{ color: '#cbd5e1', lineHeight: 1.75 }}>
                    No es un ERP, no es un almacén completo y no sustituye contabilidad o compras. Se apoya en esos datos para convertirlos en coste explicable.
                  </Typography>
                </Stack>
              </Paper>
            </Grid>
          </Grid>
        </Stack>
      </Paper>
    </Stack>
  );
}
